import { RtbCampaignSetupFlowPageModel } from '../RtbCampaignSetupFlowPageModel';
import { RtbCampaignBasicFormModel } from 'containers/RtbCampaigns/RtbCampaignSetupFlow/RtbCampaignForm/RtbCampaignBasicFormModel';
import { EditLimitationModel } from 'containers/Limitations/EditLimitationModel';
import _ from 'lodash';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager
} from 'core/rtbCampaign/RtbCampaignManager';
import { AbstractOutdoorLimitationCampaignFormModel } from '../RtbCampaignForm/OutdoorLimitationCampaignFormModel';
import { WeatherType } from 'core/limitation/Limitation';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';

// Extract the first CSV field, respecting double-quote wrapping so a quoted
// value (which may itself contain commas, and uses "" to escape a literal quote)
// is unwrapped correctly instead of keeping the surrounding quotes.
const parseFirstCsvField = (line: string): string => {
  const trimmed = line.trim();
  if (!trimmed.startsWith('"')) {
    return trimmed.split(',')[0].trim();
  }
  let result = '';
  for (let i = 1; i < trimmed.length; i++) {
    if (trimmed[i] === '"') {
      if (trimmed[i + 1] === '"') {
        result += '"';
        i++;
      } else {
        break;
      }
    } else {
      result += trimmed[i];
    }
  }
  return result;
};

export interface RtbCampaignSetupStepModel {
  readonly flowModel: RtbCampaignSetupFlowPageModel;
  readonly activeTab: number;
  validateCampaignBasic(campaign): any;
  setFormikProps(formikProps): void;
  getRtbCampaignBasicFormModel(): RtbCampaignBasicFormModel | undefined;
  getLimitationModel(): EditLimitationModel;
  onUnmount(eventHandler?: number): void;
  goNext: () => void;
  goLast: () => void;
  goSubStep: (subStepIndex: number) => void;
  downloadFixOutdoorLimitationData: () => void;
  importOutdoorDeliveryTargets: (file: File) => void;
  setTestMode: (testMode: boolean) => Promise<void>;
}

export type RtbCampaignSetupStepProps = {
  readonly model: RtbCampaignSetupStepModel;
};

export enum CampaignSetupTab {
  BASIC,
  LIMITATION,
  TRACKING
}

export class DefaultRtbCampaignSetupStepModel implements RtbCampaignSetupStepModel {
  flowModel: RtbCampaignSetupFlowPageModel;
  formikProps: any;
  goNext: () => void;

  constructor(
    flowModel: RtbCampaignSetupFlowPageModel,
    public activeTab: number,
    public goSubStep: (subStepIndex: number) => void,
    goNext: () => void,
    public goLast: () => void,
    registerValidateMethod: (validateMethod) => void,
    private rtbCampaignManager: RtbCampaignManager = new DefaultRtbCampaignManager()
  ) {
    this.flowModel = flowModel;
    this.goNext = () => {
      const limitationModel = this.getLimitationModel();
      if (this.formikProps?.values.fixOutdoorSpace) {
        this.clearWeatherLimitations(limitationModel);
      }
      limitationModel.validateRequireIncludeLimitation(true);
      if (limitationModel.state.showRequireIncludeLimitationInfo) {
        goSubStep(CampaignSetupTab.LIMITATION);
        return;
      }
      goNext();
    };
    registerValidateMethod(this.validate);
  }

  getRtbCampaignBasicFormModel(): RtbCampaignBasicFormModel | undefined {
    return this.flowModel.getRtbCampaignBasicFormModel();
  }

  getLimitationModel(): EditLimitationModel {
    return this.flowModel.getLimitationModel(false);
  }

  clearWeatherLimitations(limitationModel: EditLimitationModel) {
    const weatherTypes = Object.values(WeatherType);
    const limitationValue = limitationModel.limitationValue;
    const filterWeather = (limitations) =>
      limitations?.filter((l) => !weatherTypes.includes(l.type)) ?? [];
    limitationModel.updateLimitationValue({
      ...limitationValue,
      include: filterWeather(limitationValue.include),
      exclude: filterWeather(limitationValue.exclude),
      preferred: filterWeather(limitationValue.preferred),
      nonPreferred: filterWeather(limitationValue.nonPreferred),
      other: filterWeather(limitationValue.other)
    });
  }

  setFormikProps(formikProps) {
    this.formikProps = formikProps;
  }

  validate = async () => {
    if (!this.formikProps) {
      return [];
    }
    const campaignBasic = this.formikProps.values;
    const basicErrors = this.validateCampaignBasic(campaignBasic);
    this.setErrorsToFormik(basicErrors);
    const basicErrorNames = Object.keys(basicErrors);
    const limitationErrors = await this.flowModel
      .getLimitationModel(false)
      .validate();
    const limitationErrorNames = Object.keys(limitationErrors);
    this.goErrorSubStep(basicErrorNames, limitationErrorNames);
    return _.concat(basicErrorNames, limitationErrorNames);
  };

  setErrorsToFormik(errors) {
    this.formikProps && this.formikProps.setErrors(errors);
    const touched = {};
    this.generateFormikTouchedObj(errors, touched);
    this.formikProps && this.formikProps.setTouched(touched);
  }

  validateCampaignBasic(campaignBasic) {
    const basicFormModel = this.flowModel.getRtbCampaignBasicFormModel();
    return basicFormModel
      ? basicFormModel.validate(
          campaignBasic,
          this.flowModel.order,
          this.flowModel.localeMeta
        )
      : {};
  }

  goErrorSubStep(basicErrorNames, limitationErrorNames) {
    const errors = [...basicErrorNames];
    const basicPageErrors = _.remove(errors, (errorName) => {
      return (
        errorName !== 'videoProgressTrackingCode' &&
        errorName !== 'conversionTracking'
      );
    });
    const trackingPageErrors = errors;
    const jumpToTrackingCode =
      basicPageErrors.length === 0 &&
      limitationErrorNames.length === 0 &&
      trackingPageErrors.length > 0;
    if (jumpToTrackingCode) {
      this.goSubStep(CampaignSetupTab.TRACKING);
    } else if (basicPageErrors.length > 0) {
      this.goSubStep(CampaignSetupTab.BASIC);
    } else if (limitationErrorNames.length > 0) {
      this.goSubStep(CampaignSetupTab.LIMITATION);
    }
  }

  generateFormikTouchedObj(errors, touched) {
    if (!errors) {
      return;
    }
    const keys = Object.keys(errors);
    keys.forEach((key) => {
      const value = errors[key];
      if (typeof value === 'object') {
        touched[key] = {};
        this.generateFormikTouchedObj(errors[key], touched[key]);
      } else {
        touched[key] = true;
      }
    });
  }

  downloadFixOutdoorLimitationData = async () => {
    if (!this.flowModel.state.campaign) {
      return;
    }
    const basicFormModel = this.getRtbCampaignBasicFormModel();
    if (
      !(basicFormModel instanceof AbstractOutdoorLimitationCampaignFormModel)
    ) {
      return;
    }
    this.flowModel.updateState(true);
    const limitationModel = this.getLimitationModel();
    const deliveryUnit =
      basicFormModel.mapDeviceType === 'storeNum'
        ? DeliveryUnit.STORE
        : DeliveryUnit.DEVICE;
    await this.rtbCampaignManager.downloadFixOutdoorDeliveryTargets(
      this.flowModel.state.campaign,
      this.flowModel.l1Object.channelGroupId,
      limitationModel.limitationValue,
      deliveryUnit
    );
    this.flowModel.updateState(false);
  };

  importOutdoorDeliveryTargets = async (file: File) => {
    const text = await file.text();
    const lines = text.trim().split('\n');
    const dataLines = lines.slice(1).map(parseFirstCsvField); // skip header, keep only the first column

    const basicFormModel = this.getRtbCampaignBasicFormModel();
    if (
      !(basicFormModel instanceof AbstractOutdoorLimitationCampaignFormModel)
    ) {
      return;
    }

    const limitationModel = this.getLimitationModel();
    basicFormModel.importLimitationsFromCsvData(limitationModel, dataLines);
  };

  setTestMode = async (testMode: boolean) => {
    const basicFormModel = this.getRtbCampaignBasicFormModel();
    const isOutdoorLimitationCampaign =
      basicFormModel instanceof AbstractOutdoorLimitationCampaignFormModel;
    if (!isOutdoorLimitationCampaign) {
      return;
    }
    this.flowModel.updateState(true);
    await basicFormModel.setTestMode(testMode);
    this.flowModel.updateState(false);
  };

  onUnmount() {
    if (!this.formikProps || !this.flowModel.state.campaign) {
      return;
    }
    const limitationModel = this.flowModel.getLimitationModel(false);
    this.flowModel.setCampaign(
      {
        basic: { ...this.formikProps.values },
        limitations: limitationModel.limitationValue
      },
      true
    );
  }
}
